import React from 'react';

import { cn } from '@/lib/utils';

import { HTMLDataTrackerProps } from '@/app/types';

import iconSwitchSrc from '~/svg/IconSwitch.svg';

import { SvgIcon } from '@/components/SvgIcon';
export const sizesOptions = ['s', 'm', 'l'] as const;
const buttonSizesStyles: Record<(typeof sizesOptions)[number], string> = {
  s: 'h-4',
  m: 'h-6',
  l: 'h-[1.875rem]',
};
const switchCircleSizesStyles: Record<(typeof sizesOptions)[number], string> = {
  s: 'h-2.5 w-2.5',
  m: 'h-[1.125rem] w-[1.125rem]',
  l: 'h-6 w-6',
};

export interface SwitchProps {
  isOn: boolean;
  onToggle: () => void;
  startLabel?: React.ReactNode;
  endLabel?: React.ReactNode;
  hasIcon?: boolean;
  hasText?: boolean;
  isDisabled?: boolean;
  size?: (typeof sizesOptions)[number];
  classNames?: {
    label?: string;
    button?: string;
    buttonText?: string;
    icon?: string;
    root?: string;
  };
  ButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement> &
    HTMLDataTrackerProps;
}

function Switch({
  isOn,
  onToggle,
  startLabel,
  endLabel,
  hasIcon,
  hasText,
  isDisabled,
  classNames,
  ButtonProps,
  size = 'l',
}: SwitchProps) {
  return (
    <div className={cn('inline-flex items-center font-mono', classNames?.root)}>
      {startLabel && (
        <span
          className={cn('text-body-m mr-1.5 text-gray-700', classNames?.label)}
        >
          {startLabel}
        </span>
      )}
      <button
        disabled={isDisabled}
        data-tracker-component='switch'
        {...ButtonProps}
        className={cn(
          'relative flex min-w-14 items-center rounded-full p-[3px] transition-all duration-300 focus:outline-none',
          buttonSizesStyles[size],
          isOn ? 'bg-brand-500' : 'bg-gray-400',
          isDisabled && 'bg-gray-300',
          ButtonProps?.className,
          classNames?.button
        )}
        onClick={onToggle}
      >
        {hasText && isOn && (
          <p
            className={cn(
              'text-base-white text-body-m px-2',
              classNames?.buttonText
            )}
          >
            ON
          </p>
        )}
        <span
          className={cn(
            'bg-base-white flex transform items-center justify-center rounded-full transition-all duration-300',
            switchCircleSizesStyles[size],
            isDisabled && 'bg-gray-200',
            isOn && !hasText ? 'translate-x-[26px]' : 'translate-x-0',
            classNames?.icon
          )}
        >
          {hasIcon && (
            <SvgIcon src={iconSwitchSrc}
              className={cn(isOn ? 'text-brand-500' : 'text-gray-200')}
            />
          )}
        </span>
        {hasText && !isOn && (
          <p
            className={cn(
              'text-base-white text-body-m px-2',
              classNames?.buttonText
            )}
          >
            OFF
          </p>
        )}
      </button>
      {endLabel && (
        <span
          className={cn('text-body-m ml-1.5 text-gray-700', classNames?.label)}
        >
          {endLabel}
        </span>
      )}
    </div>
  );
}

export default Switch;
